import { useMemo } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';

import type { DTOWebExclusiveBannerTextModule } from 'src/apis/dto/DTOShopModule';
import type {
  DTOFooterSection,
  DTOGemProductSection,
  DTOShopSection,
  DTOSubProductsSection,
  DTOWebExclusiveBannerSection,
} from 'src/apis/dto/DTOShopSection';
import type { DTOShopTab } from 'src/apis/dto/DTOShopTab';
import { useGetStarterPackagePopupQuery } from 'src/hooks/useContentsMatchQuery';
import useParseStarterPackagePopup from 'src/hooks/useParseStarterPackagePopup';
import { useShopPageQueryOptions } from 'src/hooks/useShopQuery';
import type { GemProduct } from 'src/types/Payments';

import {
  toGemProducts,
  toSubscriptionProducts,
  toStarterPackageGemProduct,
  filterVisibleGemProducts,
} from './shopMappers';
import type { SubscriptionProduct } from './SubscriptionCard';

const findSection = <T extends DTOShopSection>(sections: DTOShopSection[], type: string) =>
  sections.find((s) => s.sectionType === type) as T | undefined;

export interface BannerData {
  title?: string;
  iconUrl?: string;
  modules?: DTOWebExclusiveBannerTextModule[];
}

export interface ProductSectionData<T> {
  anchor?: string;
  title?: string;
  products: T[];
}

export interface FooterData {
  items: { moduleId: string; text: string }[];
}

interface UseShopDataReturn {
  banner: BannerData | null;
  gems: ProductSectionData<GemProduct> | null;
  subscriptions: ProductSectionData<SubscriptionProduct> | null;
  footer: FooterData | null;
  tabs: DTOShopTab[];
}

const useShopData = (): UseShopDataReturn => {
  const {
    data: { shop },
  } = useSuspenseQuery(useShopPageQueryOptions());

  const { data: starterPackageData } = useGetStarterPackagePopupQuery({ refetchOnMount: true });
  const { starterPackageModule, singleProductModule } = useParseStarterPackagePopup(
    starterPackageData?.data.result.popup
  );

  const tabs = useMemo(() => shop.tabs ?? [], [shop.tabs]);
  const bannerSection = findSection<DTOWebExclusiveBannerSection>(
    shop.sections,
    'WEB_EXCLUSIVE_BANNER'
  );
  const gemSection = findSection<DTOGemProductSection>(shop.sections, 'GEM_PRODUCTS');
  const subSection = findSection<DTOSubProductsSection>(shop.sections, 'SUB_PRODUCTS');
  const footerSection = findSection<DTOFooterSection>(shop.sections, 'FOOTER');

  const banner = useMemo<BannerData | null>(() => {
    if (!bannerSection) return null;
    return {
      title: bannerSection.title,
      iconUrl: bannerSection.iconUrl,
      modules: bannerSection.modules,
    };
  }, [bannerSection]);

  const gems = useMemo<ProductSectionData<GemProduct> | null>(() => {
    if (!gemSection) return null;

    const baseProducts = toGemProducts(gemSection);
    const allProducts =
      starterPackageModule && singleProductModule
        ? [
            toStarterPackageGemProduct(starterPackageModule, singleProductModule),
            ...baseProducts,
          ].filter((p): p is GemProduct => p !== undefined)
        : baseProducts;

    const visibleProducts = filterVisibleGemProducts(allProducts);
    if (visibleProducts.length === 0) return null;

    return {
      anchor: gemSection.name,
      title: gemSection.title,
      products: visibleProducts,
    };
  }, [gemSection, starterPackageModule, singleProductModule]);

  const subscriptions = useMemo<ProductSectionData<SubscriptionProduct> | null>(() => {
    if (!subSection) return null;

    const products = toSubscriptionProducts(subSection);
    if (products.length === 0) return null;

    return {
      anchor: subSection.name,
      title: subSection.title,
      products,
    };
  }, [subSection]);

  const footer = useMemo<FooterData | null>(() => {
    if (!footerSection?.modules?.length) return null;
    return {
      items: footerSection.modules.map((m) => ({ moduleId: m.moduleId, text: m.text })),
    };
  }, [footerSection]);

  return {
    banner,
    gems,
    subscriptions,
    footer,
    tabs,
  };
};

export default useShopData;
